import React, { FC, useState, useEffect } from "react";
import { Form, FormInstance, Typography } from "antd";
import { DeprecatedRatesContent } from "../deprecated-rates/deprecated-rates-content";
import { TeamMembership, PricingType } from "../../data-types";
import { RateReplacement } from "~/gql/graphql";
import { CreateProposalScreens } from "./create-proposal";
import { VPDLabel } from "~/context/taxonomy-context";

interface DeprecatedRatesScreenProps {
  form: FormInstance;
  setScreen: (screen: CreateProposalScreens) => void;
  setRateReplacements: (replacements: RateReplacement[]) => void;
  setAllRatesReplaced?: (allReplaced: boolean) => void;
  deprecatedRates: TeamMembership[];
  matterId: number;
  clientId?: number;
  organisationalUnitId?: number;
  currency: { symbol: string; code: string };
  pricingType: PricingType;
}

export const DeprecatedRatesScreen: FC<DeprecatedRatesScreenProps> = ({
  form,
  setScreen,
  setRateReplacements,
  setAllRatesReplaced,
  deprecatedRates,
  matterId,
  clientId,
  organisationalUnitId,
  currency,
  pricingType,
}) => {
  const [localReplacements, setLocalReplacements] = useState<RateReplacement[]>([]);

  const FE_LABEL = VPDLabel("FeeEarner");

  useEffect(() => {
    if (setAllRatesReplaced) {
      const replacedRateIds = new Set(localReplacements.map((r) => r.oldRateId));

      const allReplaced =
        deprecatedRates.length > 0 &&
        deprecatedRates.every((rate) => replacedRateIds.has(rate.rateId));

      setAllRatesReplaced(allReplaced);
    }
  }, [localReplacements, deprecatedRates, setAllRatesReplaced]);

  const handleReplaceTeamMember = (data: {
    teamMemberToAdd: {
      feeEarnerId: number | null;
      feeEarnerLevelId: number | null;
      rateId: number | null;
    };
    teamMemberToRemove: { feeEarnerId: number | null; rateId: number | null };
  }) => {
    if (!data.teamMemberToRemove.rateId || !data.teamMemberToAdd.rateId) {
      return;
    }

    const newReplacement: RateReplacement = {
      oldRateId: data.teamMemberToRemove.rateId,
      newRateId: data.teamMemberToAdd.rateId,
      newFeeEarnerId: data.teamMemberToAdd.feeEarnerId,
      newFeeEarnerLevelId: data.teamMemberToAdd.feeEarnerLevelId,
    };

    const existingIndex = localReplacements.findIndex(
      (r) => r.oldRateId === newReplacement.oldRateId
    );

    if (existingIndex >= 0) {
      const updated = [...localReplacements];
      updated[existingIndex] = newReplacement;
      setLocalReplacements(updated);
    } else {
      setLocalReplacements([...localReplacements, newReplacement]);
    }

    setRateReplacements(
      existingIndex >= 0
        ? localReplacements.map((r, i) => (i === existingIndex ? newReplacement : r))
        : [...localReplacements, newReplacement]
    );
  };

  const replacedRateIds = localReplacements.map((replacement) => replacement.oldRateId);

  return (
    <div style={{ maxWidth: 900 }}>
      <Typography.Title level={4} style={{ marginBottom: 24 }}>
        Replace Deprecated Rates
      </Typography.Title>
      <Typography.Paragraph style={{ marginBottom: 24 }}>
        The historical matter you've selected contains deprecated rates or {FE_LABEL.toLowerCase()}
        s. Please choose new rates or team members to replace them before proceeding.
      </Typography.Paragraph>

      <Form form={form} layout="vertical">
        <DeprecatedRatesContent
          deprecatedRates={deprecatedRates}
          matterId={matterId}
          clientId={clientId}
          organisationalUnitId={organisationalUnitId}
          currency={currency}
          pricingType={pricingType}
          onReplaceTeamMember={handleReplaceTeamMember}
          tableHeight={350}
          replacedRateIds={replacedRateIds}
        />
      </Form>
    </div>
  );
};
